// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : react hooks for state system
// =============================================================================
// ---Third-party imports
// ---Framework imports
import { Actions, Sel } from 'uix/registries';
import { get, isNil, mapItems, dict, keys } from 'ldash';
import { useSelector, useActions, useEffect, useMemo } from 'uix/hooks';
import log from 'uix/logging';
// ---Local imports
import { loadStatesConfig } from './actions';
// ---Exports
const indexValues = (dstates)=>dict(mapItems(dstates, (statusType, l)=>[
            statusType,
            dict(mapItems(l, (name, data)=>[
                    data.value,
                    {
                        ...data,
                        name
                    }
                ]))
        ]));
const useLoaded = (dname, loadEnabled)=>{
    /* select domain states config and trigger auto load if loadEnabled == true
   */ let dstates = useSelector(Sel.states);
    let fetch = useActions(loadStatesConfig);
    useEffect(()=>{
        if (loadEnabled && isNil(dstates)) {
            //~ log(states, !states[dname])
            fetch(dname);
        }
    }, [
        dname,
        loadEnabled
    ]);
    return dstates;
};
export const useStatusType = (statusType)=>{
    let { dname } = useSelector(Sel.request);
    let dstates = useLoaded(dname, true);
    let byValue = useMemo(()=>{
        return dstates ? indexValues(dstates) : {};
    }, [
        dstates
    ]);
    if (!isNil(statusType)) {
        return {
            states: get(dstates, statusType),
            byValue: get(byValue, statusType)
        };
    } else {
        return {
            states: dstates,
            byValue
        };
    }
};
export const useStatusTypes = ()=>useStatusType();
export const useStateCounts = (statusType)=>{
    /* return counts for a state axis
  this uses tree actions/sagas
  */ let { dname } = useSelector(Sel.request);
    let availableStates = useSelector((root)=>root.data.tree.availableStates[dname]?.[statusType]);
    let getAvailableStates = useActions('getAvailableStates');
    useEffect(()=>{
        if (statusType && !availableStates) {
            getAvailableStates(dname, statusType);
        }
    }, [
        dname,
        statusType,
        availableStates
    ]);
    return availableStates;
};
export const isStateDomainVisible = (statusType, dname, dconfig, user, dstates)=>{
    /* in tree or in overviewdashboard for domain
   */ let notDisabled = (statusType)=>dconfig.states.modes[statusType] !== 'disabled';
    let visibleForGroups = (groups)=>groups.some((g)=>user.hasGroup(g, dname));
    let isConfigured = (statusType)=>dstates[statusType];
    return notDisabled(statusType) && isConfigured(statusType) && visibleForGroups(dconfig.states.visible[statusType] || []);
};
export const useVlmEnabledStates = (loadEnabled)=>{
    /* return state types that are visible in tree
   */ let user = useSelector(Sel.user);
    let { dname } = useSelector(Sel.request);
    let dconfig = useSelector(Sel.dconfig);
    let dstates = useLoaded(dname, loadEnabled);
    return keys(dstates).filter((statusType)=>isStateDomainVisible(statusType, dname, dconfig, user, dstates));
};
